/**
 * The step's targets as a vertical side rail beside the canvas.
 *
 * These began as a horizontal chip strip in the context band, which forced a
 * real drawing's 150+ targets into a single scrolling line where only a handful
 * were ever legible. A vertical list shows twenty at a time, gives each one room
 * for its subtitle, and leaves the horizontal axis to the drawing.
 */
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownUp, Plus, Search } from 'lucide-react';
import { AppIcon } from '../components/LucideIcon';
import type { TargetRailGroup, TargetRailItem, TargetSortOrder } from './targetRailItems';
import './workspace-target-rail.css';

/** The dot is the only marker of state, so the text alternative carries it. */
const STATUS_TITLE = {
  reviewed: 'reviewed',
  marked: 'marked, not reviewed',
  unmarked: 'not marked',
} as const;

export default function WorkspaceTargetRail({
  title,
  items,
  activeId,
  onSelect,
  onDrawNew,
  drawNewLabel,
  drawNewActive: drawNewActiveOverride,
  sortOrder,
  onSortOrderChange,
  panelId,
  labelledBy,
  groups,
  header,
  overlay,
  unavailable = false,
  keepEmpty = false,
}: {
  panelId?: string;
  labelledBy?: string;
  groups?: TargetRailGroup[];
  header?: ReactNode;
  overlay?: ReactNode;
  unavailable?: boolean;
  keepEmpty?: boolean;
  title: string;
  items: TargetRailItem[];
  activeId?: string | null;
  onSelect: (id: string) => void;
  /** Clears the selection so the next box drawn creates a target. */
  onDrawNew?: () => void;
  drawNewLabel?: string;
  drawNewActive?: boolean;
  /** Omitted where the order is fixed, as it is for equipment. */
  sortOrder?: TargetSortOrder;
  onSortOrderChange?: (next: TargetSortOrder) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  /* The rail replaced a dropdown that owned the search. Without it a drawing
     with 150+ targets is unnavigable, so the filter comes along. */
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(needle) ||
        (item.sub || '').toLowerCase().includes(needle),
    );
  }, [items, query]);

  /* The canvas and the Next control both move the selection, so the list has to
     follow it — otherwise the active row sits hundreds of pixels out of view. */
  useEffect(() => {
    if (!activeId) return;
    const active = listRef.current?.querySelector<HTMLElement>('.pnid-target-row--active');
    /* jsdom implements no scrollIntoView, and this is presentation only. */
    if (!active || typeof active.scrollIntoView !== 'function') return;
    active.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  if (!items.length && !keepEmpty) return null;

  const markedCount = items.filter((item) => item.status !== 'unmarked').length;
  const drawNewActive = drawNewActiveOverride ?? !activeId;

  return (
    <aside className="pnid-target-rail" aria-label={title}>
      {header}
      <div className="pnid-target-rail-head">
        <span className="pnid-target-rail-title">{title}</span>
        <span className="pnid-target-rail-count">
          {unavailable ? '—' : `${markedCount}/${items.length}`}
        </span>
      </div>
      <div className="pnid-target-rail-tools">
        <div className="pnid-target-search">
          <AppIcon icon={Search} size={14} />
          <input
            disabled={unavailable}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${title}`}
            aria-label={`Search ${title}`}
          />
        </div>
        {sortOrder && onSortOrderChange && (
          <button
            type="button"
            className="pnid-target-sort"
            aria-label={sortOrder === 'unmarked'
              ? 'Sorted unmarked first. Sort marked first'
              : 'Sorted marked first. Sort unmarked first'}
            title={sortOrder === 'unmarked' ? 'Unmarked first' : 'Marked first'}
            onClick={() => onSortOrderChange(sortOrder === 'unmarked' ? 'marked' : 'unmarked')}
          >
            <AppIcon icon={ArrowDownUp} size={13} />
            <span>{sortOrder === 'unmarked' ? 'Unmarked First' : 'Marked First'}</span>
          </button>
        )}
        {onDrawNew && (
          <button
            type="button"
            className={drawNewActive ? 'pnid-target-new pnid-target-new--active' : 'pnid-target-new'}
            aria-pressed={drawNewActive}
            onClick={onDrawNew}
          >
            <AppIcon icon={Plus} size={14} />
            <span>{drawNewLabel || 'Draw New'}</span>
          </button>
        )}
      </div>
      <div className="pnid-target-review-body" id={panelId} role={panelId ? 'tabpanel' : undefined} aria-labelledby={labelledBy}>
        <div className={unavailable ? 'pnid-target-rail-list pnid-target-rail-list--unavailable' : 'pnid-target-rail-list'}
          ref={listRef} role={keepEmpty ? undefined : 'tablist'} aria-orientation={keepEmpty ? undefined : 'vertical'} {...(unavailable ? { inert: '' } : {})} aria-hidden={unavailable || undefined}>
          {!filtered.length && (
            <p className="pnid-target-empty">
              {items.length ? `No match for “${query}”.` : `No ${title.toLowerCase()} to review.`}
            </p>
          )}
          {(groups ?? [{ id: title, label: '', items }]).map((group) => {
            const visibleItems = group.items.filter((item) => filtered.includes(item));
            if (!visibleItems.length) return null;
            return (
              <div key={group.id} className="pnid-target-group">
                {group.label && <h3 className="pnid-target-group-title">{group.label}<span>{visibleItems.length}</span></h3>}
                {visibleItems.map((item) => {
                  const active = item.id === activeId;
                  return (
                    <button key={item.id} type="button" disabled={unavailable}
                      role={keepEmpty ? undefined : 'tab'}
                      aria-selected={keepEmpty ? undefined : active}
                      aria-pressed={keepEmpty ? active : undefined}
                      className={active ? 'pnid-target-row pnid-target-row--active' : 'pnid-target-row'}
                      onClick={() => onSelect(item.id)}
                      title={`${item.sub ? `${item.label} — ${item.sub}` : item.label} (${STATUS_TITLE[item.status]})`}>
                      <span className={`pnid-target-dot pnid-target-dot--${item.status}`} aria-hidden />
                      <span className="pnid-target-text">
                        <span className="pnid-target-label">{item.label}</span>
                        {item.sub && <span className="pnid-target-sub">{item.sub}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        {overlay && <div className="pnid-target-review-overlay" role="status">{overlay}</div>}
      </div>
    </aside>
  );
}
